import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import { useEvent } from '@legacy-providers/event';
import { getCrewMember } from '@legacy-services/api/crewMembers';
import { getRegistrationCrewMembers } from '@legacy-services/api/registrationCrewMembers';

import { getCrewMemberOptionsForRegistration } from '@shared/crew-members/endpoints/getCrewMemberOptionsForRegistration';
import { useFetchCrewMember } from '@shared/crew-members/hooks/useFetchCrewMember';
import { CheckboxField, CheckboxInput } from '@shared/forms/components/CheckboxField';
import { Form, useForm } from '@shared/forms/components/Form';
import { SelectField } from '@shared/forms/components/SelectField';
import ParkingDaysField from '@shared/parking-requests/components/ParkingDaysField/ParkingDaysField';
import { getParkingRequest } from '@shared/parking-requests/endpoints/getParkingRequest';
import { getRegistrationParkingOptions } from '@shared/parking-requests/endpoints/getRegistrationParkingOptions';
import { getRegistrationParkings } from '@shared/parking-requests/endpoints/getRegistrationParkings';
import { UpdateParkingRequestData } from '@shared/parking-requests/endpoints/updateParkingRequest';

type RegistrationParkingData = App.Domain.Parking.Data.RegistrationParkingData;
type ParkingRequestData = App.Domain.Parking.Data.ParkingRequestData;

type ParkingRequestFormProps = {
  parkingRequestId: number;
  registrationId: number;
  action: (values: UpdateParkingRequestData) => Promise<ParkingRequestData>;
  onSave?: (response: ParkingRequestData) => void;
  onSaveAndReturn?: () => void;
  onCancel?: () => void;
};

export function EditParkingRequestForm({
  parkingRequestId,
  registrationId,
  action,
  onSave,
  onSaveAndReturn,
  onCancel,
}: ParkingRequestFormProps) {
  const [registrationParkings, setRegistrationParkings] = useState<null | RegistrationParkingData[]>(null);
  const [parkingRequest, setParkingRequest] = useState<ParkingRequestData>();

  const { form, fields } = useForm<UpdateParkingRequestData, ParkingRequestData>({
    source: {
      id: parkingRequestId,
      provider: ({ id }) => getParkingRequest(registrationId, id),
      onLoad: setParkingRequest,
    },
  });

  const crewMember = useFetchCrewMember(form.watch('crewMemberId'));

  const selectedParkingId = form.watch('parkingId');

  useEffect(() => {
    getRegistrationParkings(registrationId, { include: ['parking.parkingStickers'] }).then((registrationParkings) => {
      setRegistrationParkings(registrationParkings.data);
    });
  }, [registrationId]);

  const selectedRegistrationParking = registrationParkings?.find(
    (registrationParking) => registrationParking.parkingId === selectedParkingId
  );

  function handleSave(data: ParkingRequestData) {
    onSave?.(data);
    setParkingRequest(data);
  }

  return (
    <Form action={action} onSave={handleSave} onSaveAndReturn={onSaveAndReturn} onCancel={onCancel} form={form}>
      <Trans>RequestParkingMsg</Trans>

      <div className="md:grid grid-cols-2 gap-8">
        <SelectField
          name={fields.crewMemberId}
          label="Crew Member"
          options={(query) => getCrewMemberOptionsForRegistration(registrationId, query)}
          required
        />
        <SelectField
          name={fields.parkingId}
          label="Parking"
          options={(query) => getRegistrationParkingOptions(registrationId, query)}
          required
        />
      </div>

      {selectedRegistrationParking && crewMember && (
        <ParkingDaysField
          name="days"
          crewMember={crewMember}
          currentDays={parkingRequest?.days ?? []}
          currentParkingRequestParkingStickers={parkingRequest?.parkingRequestParkingStickers ?? []}
          registrationParking={selectedRegistrationParking}
        />
      )}

      <div className="text-xl text-orange-700 bg-orange-100 border border-orange-300 rounded p-4">
        <CheckboxField name={fields.claimFreedUpDays} label="Claim freed up parking days in new request." />
        <p className="text-sm mt-3 font-bold">
          Checking this checkbox will create a new parking request for the days you remove from this request. This
          ensures you do not lose any reserved parking capacity.
        </p>
      </div>
    </Form>
  );
}
